import type { RouteRecordRaw } from 'vue-router'

import { useSettingsRbac } from './composables/useSettingsRbac'
import { bindSettingsChildRoutes, registerSettingsNavEntry } from './flows/settingsNavRegistry'
import { registerWorkspaceSettingsMessages } from './i18n'
import { CHANNEL_CATALOG } from './models/channel.types'

import { useLocaleStore } from '@/shared/i18n'

/** `WorkspaceSettings` now names the `/settings` INDEX child, so every existing deep link
 *  (`{ name: 'WorkspaceSettings' }`, the health-checks `?selected_tab` remediation, the topbar
 *  workspace menu) keeps resolving while ORP-18893 does the `/settings` rename + shim. */
export const WORKSPACE_SETTINGS_ROUTE = { name: 'WorkspaceSettings' } as const

/**
 * The `/settings` children — the LIVE array `registerSettingsChildRoutes()` appends to. Bound, not
 * copied: `createRouter()` reads it by reference, so a pane registered by 30/23/15/12/11 after this
 * module was evaluated is a REAL child of the parent below and nests inside `SettingsLayout` with
 * zero imports in either direction (contract §11, closes §18 Q3).
 *
 * The host's index plus 22's own panes are seeded here; the remaining one (Notifications) joins on
 * its own ticket, with its own lazy chunk and nav entry.
 */
const settingsChildren = bindSettingsChildRoutes([
  {
    path: '',
    name: WORKSPACE_SETTINGS_ROUTE.name,
    component: () => import('./components/SettingsIndex.vue'),
  },
  {
    path: 'workspace/basics',
    name: 'SettingsWorkspaceBasics',
    component: () => import('./components/basics/WorkspaceBasicsPane.vue'),
    meta: {
      titleKey: 'workspaceSettings.basics.title',
      subtitleKey: 'workspaceSettings.basics.description',
      navKey: 'workspace.basics',
    },
  },
  {
    path: 'account',
    name: 'SettingsAccount',
    component: () => import('./components/account/AccountPane.vue'),
    meta: { titleKey: 'workspaceSettings.account.title', navKey: 'account' },
  },
  {
    path: 'profile',
    name: 'SettingsProfile',
    component: () => import('./components/profile/ProfilePane.vue'),
    meta: { titleKey: 'workspaceSettings.profile.title', navKey: 'profile' },
  },
  {
    path: 'notifications',
    name: 'SettingsNotifications',
    component: () => import('./components/notifications/NotificationsPane.vue'),
    // ⚠ Contract §11 also declares `permission: [4 keys]` here. It is dropped deliberately:
    // `app/router/meta.ts:16` types `permission?: string` and `permissionAccess.ts:26-28` passes it
    // straight to `hasPermission(key)`, so an ARRAY does not typecheck and — forced through —
    // becomes a lookup miss, i.e. denied and redirected home. The gate lives in the pane as 22's
    // `forbidden` state instead, which is the module's own rule (§12/§14) and costs no cross-module
    // change. See the header of `components/notifications/NotificationsPane.vue`.
    meta: { titleKey: 'workspaceSettings.notifications.title', navKey: 'notifications' },
  },
  {
    path: 'permissions',
    name: 'SettingsPermissions',
    component: () => import('./components/permissions/PermissionsPane.vue'),
    meta: { titleKey: 'workspaceSettings.permissions.title', navKey: 'permissions' },
  },
])

/**
 * 22's own panes take a nav entry through the SAME registry a registrant uses — `useSettingsNav` has
 * exactly one reader and there is no second code path for the host's panes (§9). Order 10 sits in
 * 22's reserved 0–99 band; registrants use 100+.
 *
 * ⚠ Registering the FIRST entry changes `/settings` globally: `SettingsIndex` redirects to
 * `firstVisibleEntry` the moment one exists, so the transitional `WorkspaceSettingsPage` landing
 * becomes unreachable for anyone who can edit the workspace. That hand-off is the point of this
 * ticket, not a side effect.
 *
 * `visible` is gated by `canEditWorkspace`, which is §12's rule for the whole `workspace` group — a
 * viewer loses the nav entry but can still deep-link the pane and see it read-only (§12 defence in
 * depth: hidden in the nav, degraded in the pane, refused by the backend).
 */
registerSettingsNavEntry({
  key: 'workspace.basics',
  labelKey: 'workspaceSettings.nav.basics',
  icon: 'gear',
  group: 'workspace',
  order: 10,
  routeName: 'SettingsWorkspaceBasics',
  visible: () => useSettingsRbac().canEditWorkspace.value,
})

/** Notifications is gated PER CHANNEL, so its nav entry is visible as soon as ANY of the four
 *  channel permissions is held (`useWorkspaceSettings.js:203-208`) — not on `canEditWorkspace`,
 *  because the channel permissions are package flags, not a role. Same predicate as the pane's
 *  `forbidden` state, read off the same catalog, so the nav and the pane cannot disagree. */
registerSettingsNavEntry({
  key: 'notifications',
  labelKey: 'workspaceSettings.nav.notifications',
  icon: 'bolt',
  group: 'workspace',
  order: 90,
  routeName: 'SettingsNotifications',
  visible: () => {
    const { can } = useSettingsRbac()
    return CHANNEL_CATALOG.some((descriptor) => can(descriptor.permission))
  },
})

export const workspaceSettingsRoutes: RouteRecordRaw[] = [
  {
    path: 'settings',
    component: () => import('./components/SettingsLayout.vue'),
    // Shell composition sits on the PARENT so every settings child inherits it through
    // vue-router's matched-meta merge: the sidebar leaf stays active across the whole surface,
    // the desktop topbar leaves navigation to the Settings takeover, and a pane only declares a
    // `titleKey` when it wants a different heading.
    // `settingsNav` is 05's own typed flag (`AppRouteMeta`) — it is what drives the sidebar
    // takeover, so a pane registered by 30/23/15/12/11 inherits the takeover for free.
    meta: {
      requiresAuth: true,
      navKey: 'workspaceSettings',
      titleKey: 'workspaceSettings.page.title',
      settingsNav: true,
      topbarNavigation: 'mobile-only',
    },
    beforeEnter: () => registerWorkspaceSettingsMessages(useLocaleStore().locale),
    children: settingsChildren,
  },
]
